import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE;

const DURATION_OPTIONS = [
    { label: '15 Minuten', minutes: 15 },
    { label: '30 Minuten', minutes: 30 },
    { label: '1 Stunde', minutes: 60 },
    { label: '6 Stunden', minutes: 360 },
    { label: '12 Stunden', minutes: 720 },
    { label: '1 Tag', minutes: 1440 },
    { label: '3 Tage', minutes: 4320 },
    { label: '7 Tage', minutes: 10080 }
];

const Giveaway = () => {
    const [channels, setChannels] = useState([]);
    const [selectedChannelId, setSelectedChannelId] = useState('');
    const [prize, setPrize] = useState('');
    const [winners, setWinners] = useState(1);
    const [durationMinutes, setDurationMinutes] = useState(60);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        loadChannels();
    }, []);

    const loadChannels = async () => {
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/discord/channels`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setChannels(response.data.channels || []);
            }
        } catch (err) {
            console.error('Fehler beim Laden der Channels:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedChannelId || !prize.trim()) {
            setError('Bitte Channel und Preis ausfüllen.');
            return;
        }
        if (winners < 1) {
            setError('Anzahl der Gewinner muss mindestens 1 sein.');
            return;
        }
        if (!durationMinutes || durationMinutes < 1) {
            setError('Bitte eine gültige Dauer auswählen.');
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccess(false);
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.post(`${API_BASE}/api/giveaway`, {
                channelId: selectedChannelId,
                prize: prize.trim(),
                winners: Number(winners),
                durationMinutes: Number(durationMinutes)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setSuccess(true);
                setPrize('');
                setWinners(1);
                setDurationMinutes(60);
                setSelectedChannelId('');
            } else {
                throw new Error(response.data.error || 'Fehler beim Erstellen des Giveaways');
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Fehler beim Erstellen des Giveaways');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="dashboard-main">
            <div className="hero-panel">
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.8rem' }}>🎁 Giveaway</h2>
                    <p className="muted small">Erstelle und verwalte Discord Giveaways</p>
                </div>
            </div>

            <div className="grid">
                <div className="card">
                    <div className="card-header">
                        <h4>Neues Giveaway erstellen</h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="giveaway-channel">Discord Channel</label>
                                <select
                                    id="giveaway-channel"
                                    value={selectedChannelId}
                                    onChange={(e) => setSelectedChannelId(e.target.value)}
                                    disabled={isLoading}
                                    style={{ width: '100%', padding: '10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '5px' }}
                                >
                                    <option value="">-- Channel auswählen --</option>
                                    {channels.map(ch => (
                                        <option key={ch.id} value={ch.id}>
                                            {ch.category ? `[${ch.category}] ` : ''}{ch.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="giveaway-prize">Preis</label>
                                <input
                                    id="giveaway-prize"
                                    type="text"
                                    placeholder="Was gibt es zu gewinnen?"
                                    value={prize}
                                    onChange={(e) => setPrize(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="giveaway-duration">Laufzeit</label>
                                <select
                                    id="giveaway-duration"
                                    value={durationMinutes}
                                    onChange={(e) => setDurationMinutes(Number(e.target.value))}
                                    disabled={isLoading}
                                    style={{ width: '100%', padding: '10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '5px' }}
                                >
                                    {DURATION_OPTIONS.map(opt => (
                                        <option key={opt.minutes} value={opt.minutes}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="giveaway-winners">Anzahl der Gewinner</label>
                                <input
                                    id="giveaway-winners"
                                    type="number"
                                    min="1"
                                    value={winners}
                                    onChange={(e) => setWinners(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            {error && (
                                <div className="alert alert-error">
                                    <span className="alert-icon">⚠️</span>
                                    <span>{error}</span>
                                </div>
                            )}

                            {success && (
                                <div className="alert alert-success">
                                    <span className="alert-icon">✅</span>
                                    <span>Giveaway erfolgreich erstellt!</span>
                                </div>
                            )}

                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isLoading || !selectedChannelId || !prize.trim() || winners < 1 || durationMinutes < 1}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-sm"></span>
                                            Wird erstellt...
                                        </>
                                    ) : (
                                        'Giveaway erstellen'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Giveaway;
